import { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { TaraProvider } from '@/contexts/TaraContext';
import { TaraFab } from '@/components/tara/TaraFab';
import { TaraPanel } from '@/components/tara/TaraPanel';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppShell } from '@/components/AppShell';
import { AppShellSkeleton } from '@/components/AppShellSkeleton';
import AndBookSkeleton, { AndBookRouteFallback } from '@/components/andbook/AndBookSkeleton';
import { useAuth } from '@/hooks/useAuth';
import { initAnalytics, capturePageview } from '@/utils/analytics';
import { api } from '@/api/client';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { ScreenWatermark } from '@/watermark/ScreenWatermark';

const PAGEVIEW_SKIP = new Set(['/login', '/callback', '/test-429']);

initAnalytics();

function DefaultRedirect(): JSX.Element {
  // Every signed-in user lands on the Platforms launcher (mounted at /home).
  return <Navigate to="/home" replace />;
}

// Global TARA assistant — the FAB + slide-in panel. Only mounted for signed-in
// users. The panel routes through the backend proxy (/api/tara/*); the browser
// never holds the TARA service token, and user_id is injected server-side.
function TaraGlobal(): JSX.Element | null {
  const { isAuthenticated } = useAuth();
  // TARA is on by default (dev) and disabled per environment by setting
  // VITE_ENABLE_TARA='false' (prod). Backend also refuses without a service token.
  if (import.meta.env.VITE_ENABLE_TARA === 'false') return null;
  if (!isAuthenticated) return null;
  return (
    <>
      <TaraFab />
      <TaraPanel />
    </>
  );
}

function AndBookShell(): JSX.Element {
  useEffect(() => {
    const id = 'andbook-fonts';
    if (document.getElementById(id)) return;
    const pre = document.createElement('link');
    pre.rel = 'preconnect';
    pre.href = 'https://fonts.googleapis.com';
    document.head.appendChild(pre);
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Figtree:wght@400;500;600;700;800&display=swap';
    document.head.appendChild(link);
  }, []);

  return (
    <AppShell title="AndBook" noPadding>
      <Suspense fallback={<AndBookRouteFallback />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}

function ScrollToTop(): null {
  const { pathname } = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    window.scrollTo(0, 0);
    capturePageview(pathname);
    // Only ping our own /api/track/pageview when the user is authenticated;
    // otherwise the request 401s and clutters the console with noise on /login etc.
    if (user && !PAGEVIEW_SKIP.has(pathname)) {
      api.post('/api/track/pageview', { path: pathname }).catch(() => {});
    }
  }, [pathname, user]);
  return null;
}

const LoginPage = lazyWithRetry(() => import('@/pages/LoginPage'));
const AuthCallbackPage = lazyWithRetry(() => import('@/pages/AuthCallbackPage'));
const IntroVideoPage = lazyWithRetry(() => import('@/pages/IntroVideoPage'));
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage'));
const MyRecordsPage = lazyWithRetry(() => import('@/pages/MyRecordsPage'));
const OrgOverviewPage = lazyWithRetry(() => import('@/pages/OrgOverviewPage'));
const SettingsPage = lazyWithRetry(() => import('@/pages/SettingsPage'));
const PasskeysPage = lazyWithRetry(() => import('@/pages/PasskeysPage'));
const AdminUsersPage = lazyWithRetry(() => import('@/pages/admin/UsersPage'));
const AdminDepartmentsPage = lazyWithRetry(() => import('@/pages/admin/DepartmentsPage'));
const AdminLocationsPage = lazyWithRetry(() => import('@/pages/admin/LocationsPage'));
const AdminHolidaysPage = lazyWithRetry(() => import('@/pages/admin/HolidaysPage'));
const HolidaysPage = lazyWithRetry(() => import('@/pages/HolidaysPage'));
const AdminUserDetailPage = lazyWithRetry(() => import('@/pages/admin/UserDetailPage'));
const AdminAuditLogPage = lazyWithRetry(() => import('@/pages/admin/AuditLogPage'));
const AdminEmailLogPage = lazyWithRetry(() => import('@/pages/admin/EmailLogPage'));
const AdminRequestsPage = lazyWithRetry(() => import('@/pages/admin/RequestsPage'));
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/DashboardPage'));
const AdminAttendanceTypesPage = lazyWithRetry(() => import('@/pages/admin/AttendanceTypesPage'));
const AdminMarkAttendancePage = lazyWithRetry(() => import('@/pages/admin/MarkAttendancePage'));
const AdminQrGeneratorPage = lazyWithRetry(() => import('@/pages/admin/QrGeneratorPage'));
const AdminSettingsPage = lazyWithRetry(() => import('@/pages/admin/SettingsPage'));
const AdminAnnouncementsPage = lazyWithRetry(() => import('@/pages/admin/AnnouncementsPage'));
const AdminOrgChartPage = lazyWithRetry(() => import('@/pages/admin/OrgChartPage'));
const OrgChartPage = lazyWithRetry(() => import('@/pages/OrgChartPage'));
const TeamDashboardPage = lazyWithRetry(() => import('@/pages/TeamDashboardPage'));
const MyRequestsPage = lazyWithRetry(() => import('@/pages/MyRequestsPage'));
const LeaveTypesPage = lazyWithRetry(() => import('@/pages/LeaveTypesPage'));
const MyLeavesPage = lazyWithRetry(() => import('@/pages/MyLeavesPage'));
const AdminLeaveBalancesPage = lazyWithRetry(() => import('@/pages/admin/LeaveBalancesPage'));
const AdminLeaveSettingsPage = lazyWithRetry(() => import('@/pages/admin/LeaveSettingsPage'));
const AttendanceCalendarPage = lazyWithRetry(() => import('@/pages/AttendanceCalendarPage'));
const NotificationsPage = lazyWithRetry(() => import('@/pages/NotificationsPage'));
const ProfilePage = lazyWithRetry(() => import('@/pages/ProfilePage'));
const FaqPage = lazyWithRetry(() => import('@/pages/FaqPage'));
const WhatsNewPage = lazyWithRetry(() => import('@/pages/WhatsNewPage'));
const ProjectsPage = lazyWithRetry(() => import('@/pages/ProjectsPage'));
const DocumentsPage = lazyWithRetry(() => import('@/pages/DocumentsPage'));
const PlatformsPage = lazyWithRetry(() => import('@/pages/PlatformsPage'));
const AdminProjectsPage = lazyWithRetry(() => import('@/pages/admin/ProjectsPage'));
const AdminAnalyticsPage = lazyWithRetry(() => import('@/pages/admin/AnalyticsPage'));
const AdminInventoryPage = lazyWithRetry(() => import('@/pages/admin/InventoryPage'));
const AdminInventoryUserPage = lazyWithRetry(() => import('@/pages/admin/InventoryUserPage'));
const AdminInventoryDevicePage = lazyWithRetry(() => import('@/pages/admin/InventoryDevicePage'));
const AdminInventoryCategoryPage = lazyWithRetry(() => import('@/pages/admin/InventoryCategoryPage'));
const AdminInventoryTypePage = lazyWithRetry(() => import('@/pages/admin/InventoryTypePage'));
const MyInventoryPage = lazyWithRetry(() => import('@/pages/MyInventoryPage'));
const AndTimesPage = lazyWithRetry(() => import('@/pages/AndTimesPage'));
const AndBookDashboardPage = lazyWithRetry(() => import('@/pages/AndBookDashboardPage'));
const AndBookBrowsePage = lazyWithRetry(() => import('@/pages/AndBookBrowsePage'));
const AndBookSingleBedPage = lazyWithRetry(() => import('@/pages/AndBookSingleBedPage'));
const AndBookMyBookingsPage = lazyWithRetry(() => import('@/pages/AndBookMyBookingsPage'));
const AndBookCalendarPage = lazyWithRetry(() => import('@/pages/AndBookCalendarPage'));
const AndBookApprovalsPage = lazyWithRetry(() => import('@/pages/AndBookApprovalsPage'));
const AndBookAllBookingsPage = lazyWithRetry(() => import('@/pages/AndBookAllBookingsPage'));
const AndBookExplorePage = lazyWithRetry(() => import('@/pages/AndBookExplorePage'));
const AndBookTwinBedPage = lazyWithRetry(() => import('@/pages/AndBookTwinBedPage'));
const AndBookOfficeRoomsPage = lazyWithRetry(() => import('@/pages/AndBookOfficeRoomsPage'));
const AndBookOfficeRoomDetailPage = lazyWithRetry(() => import('@/pages/AndBookOfficeRoomDetailPage'));
const AndBookSosPage = lazyWithRetry(() => import('@/pages/AndBookSosPage'));
const AndBookSettingsPage = lazyWithRetry(() => import('@/pages/AndBookSettingsPage'));
const AndBookNotificationsPage = lazyWithRetry(() => import('@/pages/AndBookNotificationsPage'));
const GlobalNotificationsPage = lazyWithRetry(() => import('@/pages/GlobalNotificationsPage'));
const AndPlayPage = lazyWithRetry(() => import('@/pages/AndPlayPage'));
const AndPlayQuizPage = lazyWithRetry(() => import('@/pages/AndPlayQuizPage'));
const AndPlayWordlePage = lazyWithRetry(() => import('@/pages/AndPlayWordlePage'));
const AndPlayLeaderboardPage = lazyWithRetry(() => import('@/pages/AndPlayLeaderboardPage'));
const AdminAndTimesPage = lazyWithRetry(() => import('@/pages/admin/AndTimesPage'));
const NotFoundPage = lazyWithRetry(() => import('@/pages/NotFoundPage'));
const Test429Page = lazyWithRetry(() => import('@/pages/Test429Page'));
const PalletRossPage = lazyWithRetry(() => import('@/pages/PalletRossPage'));
const PalletRossLandingPage = lazyWithRetry(() => import('@/pages/PalletRossLandingPage'));
const AdminPlatformSettingsPage = lazyWithRetry(() => import('@/pages/admin/PlatformSettingsPage'));
const AdminWatermarkSettingsPage = lazyWithRetry(() => import('@/pages/admin/WatermarkSettingsPage'));

export default function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <SidebarProvider>
        <TaraProvider>
        <ScrollToTop />
        <ScreenWatermark />
        <Suspense fallback={<AppShellSkeleton />}>
          <Routes>
            <Route path="/login" element={<Suspense fallback={null}><LoginPage /></Suspense>} />
            <Route path="/callback" element={<Suspense fallback={null}><AuthCallbackPage /></Suspense>} />
            <Route path="/intro" element={<ProtectedRoute><IntroVideoPage /></ProtectedRoute>} />

            <Route path="/home" element={<ProtectedRoute><PlatformsPage /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><GlobalNotificationsPage /></ProtectedRoute>} />
            <Route path="/andone/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/andone/my-records" element={<ProtectedRoute><MyRecordsPage /></ProtectedRoute>} />
            <Route path="/andone/my-requests" element={<ProtectedRoute><MyRequestsPage /></ProtectedRoute>} />
            <Route path="/andone/leave-types" element={<ProtectedRoute><LeaveTypesPage /></ProtectedRoute>} />
            <Route path="/andone/my-leaves" element={<ProtectedRoute><MyLeavesPage /></ProtectedRoute>} />
            <Route path="/andone/my-calendar" element={<ProtectedRoute><AttendanceCalendarPage /></ProtectedRoute>} />
            <Route path="/andone/holidays" element={<ProtectedRoute><HolidaysPage /></ProtectedRoute>} />
            <Route path="/andone/my-inventory" element={<ProtectedRoute><MyInventoryPage /></ProtectedRoute>} />
            <Route path="/andone/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/andone/passkeys" element={<ProtectedRoute><PasskeysPage /></ProtectedRoute>} />
            <Route path="/andone/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/andone/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/andone/faq" element={<ProtectedRoute><FaqPage /></ProtectedRoute>} />
            <Route path="/andone/whats-new" element={<ProtectedRoute><WhatsNewPage /></ProtectedRoute>} />
            <Route path="/andone/org-chart" element={<ProtectedRoute><OrgChartPage /></ProtectedRoute>} />
            <Route path="/anddocs" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
            <Route path="/andone/documents" element={<Navigate to="/anddocs" replace />} />

            <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
            <Route path="/platforms" element={<ProtectedRoute><PlatformsPage /></ProtectedRoute>} />
            <Route path="/andtimes" element={<ProtectedRoute><AndTimesPage /></ProtectedRoute>} />
            <Route path="/andbook" element={<ProtectedRoute><AndBookShell /></ProtectedRoute>}>
              <Route index element={<Navigate to={`dashboard${typeof window !== 'undefined' ? window.location.search : ''}`} replace />} />
              <Route path="dashboard" element={<Suspense fallback={<AndBookSkeleton />}><AndBookDashboardPage /></Suspense>} />
              <Route path="book" element={<AndBookBrowsePage />} />
              <Route path="book/room/:roomName" element={<AndBookTwinBedPage />} />
              <Route path="book/:spaceId" element={<AndBookSingleBedPage />} />
              <Route path="my-bookings" element={<AndBookMyBookingsPage />} />
              <Route path="calendar" element={<AndBookCalendarPage />} />
              <Route path="explore" element={<AndBookExplorePage />} />
              <Route path="rooms" element={<AndBookOfficeRoomsPage />} />
              <Route path="rooms/:roomId" element={<AndBookOfficeRoomDetailPage />} />
              <Route path="approvals" element={<ProtectedRoute roles={['Admin', 'SuperAdmin', 'HR']} anyPermissions={['booking:manage', 'booking:approve']}><AndBookApprovalsPage /></ProtectedRoute>} />
              <Route path="all-bookings" element={<ProtectedRoute roles={['Admin', 'SuperAdmin', 'HR']} anyPermissions={['booking:manage', 'booking:approve']}><AndBookAllBookingsPage /></ProtectedRoute>} />
              <Route path="sos" element={<ProtectedRoute roles={['Admin', 'SuperAdmin', 'HR']} anyPermissions={['booking:sos']}><AndBookSosPage /></ProtectedRoute>} />
              <Route path="settings" element={<ProtectedRoute roles={['Admin', 'SuperAdmin', 'HR']} anyPermissions={['booking:manage']}><AndBookSettingsPage /></ProtectedRoute>} />
              <Route path="notifications" element={<AndBookNotificationsPage />} />
            </Route>
            <Route path="/andplay" element={<ProtectedRoute><AndPlayPage /></ProtectedRoute>} />
            <Route path="/andplay/quiz" element={<ProtectedRoute><AndPlayQuizPage /></ProtectedRoute>} />
            <Route path="/andplay/wordle" element={<ProtectedRoute><AndPlayWordlePage /></ProtectedRoute>} />
            <Route path="/andplay/leaderboard" element={<ProtectedRoute><AndPlayLeaderboardPage /></ProtectedRoute>} />
            <Route path="/andtimes/quiz" element={<Navigate to="/andplay/quiz" replace />} />
            <Route path="/andtimes/wordle" element={<Navigate to="/andplay/wordle" replace />} />
            <Route path="/andtimes/leaderboard" element={<Navigate to="/andplay/leaderboard" replace />} />
            <Route path="/andone/org-overview" element={<ProtectedRoute permissions={['attendance:view']}><OrgOverviewPage /></ProtectedRoute>} />
            <Route path="/andone/team-dashboard" element={<ProtectedRoute permissions={['attendance:view']}><TeamDashboardPage /></ProtectedRoute>} />

            <Route path="/andone/admin/users" element={<ProtectedRoute permissions={['users:view']}><AdminUsersPage /></ProtectedRoute>} />
            <Route path="/andone/admin/users/:userId" element={<ProtectedRoute permissions={['users:view']}><AdminUserDetailPage /></ProtectedRoute>} />
            <Route path="/andone/admin/qr-generator" element={<ProtectedRoute permissions={['users:view']}><AdminQrGeneratorPage /></ProtectedRoute>} />
            <Route path="/andone/admin/departments" element={<ProtectedRoute permissions={['departments:view']}><AdminDepartmentsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/locations" element={<ProtectedRoute permissions={['locations:view']}><AdminLocationsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/holidays" element={<ProtectedRoute permissions={['holidays:view']}><AdminHolidaysPage /></ProtectedRoute>} />
            <Route path="/andone/admin/audit-log" element={<ProtectedRoute permissions={['audit-log:view']}><AdminAuditLogPage /></ProtectedRoute>} />
            <Route path="/andone/admin/email-log" element={<ProtectedRoute permissions={['email-log:view']}><AdminEmailLogPage /></ProtectedRoute>} />
            <Route path="/andone/admin/requests" element={<ProtectedRoute permissions={['requests:view']}><AdminRequestsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/leave-balances" element={<ProtectedRoute permissions={['users:view']}><AdminLeaveBalancesPage /></ProtectedRoute>} />
            <Route path="/andone/admin/leave-settings" element={<ProtectedRoute permissions={['users:view']}><AdminLeaveSettingsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/attendance-types" element={<ProtectedRoute permissions={['attendance-types:view']}><AdminAttendanceTypesPage /></ProtectedRoute>} />
            <Route path="/andone/admin/mark-attendance" element={<ProtectedRoute permissions={['attendance:edit']}><AdminMarkAttendancePage /></ProtectedRoute>} />
            <Route path="/andone/admin/settings" element={<ProtectedRoute permissions={['notification-settings:view']}><AdminSettingsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/announcements" element={<ProtectedRoute permissions={['notification-settings:view']}><AdminAnnouncementsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/dashboard" element={<ProtectedRoute permissions={['dashboard:view']}><AdminDashboardPage /></ProtectedRoute>} />
            <Route path="/andone/admin/org-chart" element={<ProtectedRoute permissions={['users:view']}><AdminOrgChartPage /></ProtectedRoute>} />
            <Route path="/andone/admin/projects" element={<ProtectedRoute permissions={['users:view']}><AdminProjectsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/inventory" element={<ProtectedRoute permissions={['inventory:view']}><AdminInventoryPage /></ProtectedRoute>} />
            <Route path="/andone/admin/inventory/types/:typeId" element={<ProtectedRoute permissions={['inventory:view']}><AdminInventoryTypePage /></ProtectedRoute>} />
            <Route path="/andone/admin/inventory/categories/:categoryId" element={<ProtectedRoute permissions={['inventory:view']}><AdminInventoryCategoryPage /></ProtectedRoute>} />
            <Route path="/andone/admin/inventory/devices/:itemId" element={<ProtectedRoute permissions={['inventory:view']}><AdminInventoryDevicePage /></ProtectedRoute>} />
            <Route path="/andone/admin/inventory/users/:userId" element={<ProtectedRoute permissions={['inventory:view']}><AdminInventoryUserPage /></ProtectedRoute>} />
            <Route path="/andone/admin/analytics" element={<ProtectedRoute permissions={['analytics:view']}><AdminAnalyticsPage /></ProtectedRoute>} />
            <Route path="/andone/admin/andtimes" element={<ProtectedRoute roles={['SuperAdmin']}><AdminAndTimesPage /></ProtectedRoute>} />

            <Route path="/admin/settings" element={<ProtectedRoute roles={['Admin', 'SuperAdmin', 'AdminViewer']}><AdminPlatformSettingsPage /></ProtectedRoute>} />
            <Route path="/admin/settings/watermark" element={<ProtectedRoute roles={['Admin', 'SuperAdmin', 'AdminViewer']}><AdminWatermarkSettingsPage /></ProtectedRoute>} />

            <Route path="/test-429" element={<Suspense fallback={null}><Test429Page /></Suspense>} />
            <Route path="/welcome" element={<Suspense fallback={null}><PalletRossPage /></Suspense>} />
            <Route path="/pallet-ross-landing" element={<Suspense fallback={null}><PalletRossLandingPage /></Suspense>} />
            <Route path="/" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
        <TaraGlobal />
        </TaraProvider>
        </SidebarProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
